import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../context/LanguageContext";
import { api, bookUrl, stripNikud } from "../lib/api";
import { bookHe, numberToHebrew } from "../lib/hebrewRef";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { useBasket } from "../context/BasketContext";
import { BookOpen, Scroll, Sparkles, Feather, ArrowLeft, ArrowRight, ShoppingBasket, NotebookPen, Calendar as CalendarIcon, RotateCcw } from "lucide-react";
import { Button } from "../components/ui/button";
import BookCover from "../components/BookCover";
import SaveToNotebookButton from "../components/SaveToNotebookButton";
import ParashaChiddushDialog from "../components/parasha/ParashaChiddushDialog";
import ParashaContentDialog from "../components/parasha/ParashaContentDialog";
import JewishDayBanner from "../components/calendar/JewishDayBanner";
import { formatHebrewRef } from "../lib/hebrewRef";
import { toast } from "sonner";

export default function Landing() {
  const { t } = useTranslation();
  const { isEn } = useLanguage();
  const { user } = useAuth();
  const { update } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { add: addToBasket } = useBasket();
  const [daily, setDaily] = useState(null);
  const [progress, setProgress] = useState(null);
  const [parasha, setParasha] = useState(null);
  const [parashaAdding, setParashaAdding] = useState(false);
  // Modal triggers — "ממקורות חז״ל" + "סיפור לפרשה" (Feb 28, 2026).
  // Story is admin-gated until rabbinical review completes; aggadah
  // is public-facing per the existing reviewed pipeline.
  const [parashaContentMode, setParashaContentMode] = useState(null); // null | "aggadah" | "story"
  // Parasha list (54 weekly) + the slug of THIS week's parasha so the
  // "back to current" button knows where to land. `parashaOffset` is
  // signed: 0 = this week, -1 = previous, +1 = next. The card's
  // displayed payload always lives in `parasha`.
  const [parashaList, setParashaList] = useState([]);
  const [currentSlug, setCurrentSlug] = useState(null);
  const [parashaOffset, setParashaOffset] = useState(0);
  const [parashaSwapping, setParashaSwapping] = useState(false);

  // Pick the BookCover render size from the viewport so the hero
  // book never overflows on phones or tablets.
  //
  // Updated Feb 2026: added a tablet step. At iPad-portrait widths
  // (834px) the col-span-5 cell is only ~320px wide — an `lg` book
  // (420px) overflows into the text column. The new thresholds:
  //   <1024 (phones + iPad portrait)         → "md" (320×440)
  //   1024-1279 (iPad landscape + small tab) → "lg" (420×580)
  //   ≥1280 (small laptop +)                 → "xl" (520×720)
  const [bookSize, setBookSize] = useState(() => {
    if (typeof window === "undefined") return "xl";
    const w = window.innerWidth;
    if (w < 1024) return "md";
    if (w < 1280) return "lg";
    return "xl";
  });
  useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      if (w < 1024) setBookSize("md");
      else if (w < 1280) setBookSize("lg");
      else setBookSize("xl");
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    api.dailyVerse().then(setDaily).catch(() => {});
    // If the URL carries ?parasha=<slug>, jump straight to that
    // parasha card (useful for shared / bookmarked links). Otherwise
    // load this week's parasha.
    const overrideSlug = searchParams.get("parasha");
    api.parashaCurrent().then((p) => {
      setCurrentSlug(p?.slug || null);
      if (overrideSlug && overrideSlug !== p?.slug) {
        api.parashaCardBySlug(overrideSlug)
          .then((override) => {
            setParasha(override);
            // Offset stays 0; the "back to current" button is conditioned
            // on currentSlug !== parasha.slug below so it surfaces
            // automatically while we're browsing an override.
            setParashaOffset(0);
          })
          .catch(() => {
            setParasha(p);
            setParashaOffset(0);
          });
      } else {
        setParasha(p);
        setParashaOffset(0);
      }
    }).catch(() => {});
    api.parashaList().then((d) => {
      // BE returns { parashot: [...] } — 54 weekly parashot in order.
      setParashaList(Array.isArray(d?.parashot) ? d.parashot : []);
    }).catch(() => {});
    if (user) api.getProgress().then(setProgress).catch(() => {});
  }, [user, searchParams]);

  // Navigate the parasha card by `delta` weeks (signed). Wraps around
  // — after וזאת הברכה we loop back to בראשית, and vice versa, exactly
  // as the user requested (the Torah is read in an annual cycle).
  const navigateParasha = (delta) => {
    if (parashaSwapping) return;
    if (!parashaList.length || !currentSlug) return;
    const currentIdx = parashaList.findIndex((p) => p.slug === currentSlug);
    if (currentIdx < 0) return;
    const newOffset = parashaOffset + delta;
    // Wrap on the absolute index so we always land on a real parasha.
    const targetIdx = (((currentIdx + newOffset) % parashaList.length) + parashaList.length) % parashaList.length;
    const targetSlug = parashaList[targetIdx]?.slug;
    if (!targetSlug) return;
    setParashaSwapping(true);
    api.parashaCardBySlug(targetSlug)
      .then((p) => {
        setParasha(p);
        setParashaOffset(newOffset);
      })
      .catch(() => {
        toast.error("לא הצלחנו לטעון את הפרשה השכנה");
      })
      .finally(() => {
        // Tiny grace period so the fade transition feels intentional.
        setTimeout(() => setParashaSwapping(false), 180);
      });
  };

  const resetToCurrentParasha = () => {
    if (parashaSwapping || parashaOffset === 0 || !currentSlug) return;
    setParashaSwapping(true);
    api.parashaCurrent()
      .then((p) => {
        setParasha(p);
        setParashaOffset(0);
      })
      .catch(() => {})
      .finally(() => {
        setTimeout(() => setParashaSwapping(false), 180);
      });
  };

  const handleAddParashaToBasket = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!parasha) return;
    setParashaAdding(true);
    try {
      const res = await api.parashaAddToBasket(parasha.slug);
      const items = res.items || [];
      for (const item of items) {
        addToBasket({
          type: "verse",
          book: item.book,
          book_he: item.book_he,
          chapter: item.chapter,
          verse: item.verse,
          he: item.he || "",
          en: item.en || "",
        });
      }
      toast.success(`פרשת ${parasha.he} נוספה לסל`, {
        description: `${items.length} פסוקים נוספו · כעת בנה שיעור תורה משלך`,
        duration: 5000,
      });
    } catch (err) {
      toast.error("שגיאה בהוספת הפרשה", {
        description: err.response?.data?.detail || "נסה שוב",
      });
    } finally {
      setParashaAdding(false);
    }
  };

  const openBook = () => {
    // Big home-page book always opens Genesis 1 — it represents "the Torah"
    // as the canonical starting point. Continued reading is exposed as a
    // separate "המשך בקריאה" pill below for users with prior progress.
    update({ readingMode: "book" });
    sessionStorage.removeItem("tanakh_book_opened");
    navigate("/reader/Genesis/1");
  };

  return (
    <div className="relative" data-testid="landing-page">
      {/* Smart Jewish-day banner — silent on ordinary days, prominent
          for erev chag / chag / fast / Rosh Chodesh / erev Shabbat. */}
      <JewishDayBanner />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 pt-12 pb-16 md:pt-16 md:pb-20 lg:pt-24 lg:pb-28 xl:pt-28 xl:pb-32">
          <div className="grid gap-8 md:gap-10 lg:gap-12 md:grid-cols-12 md:items-center">
            <div className="md:col-span-7 space-y-6 md:space-y-7 lg:space-y-8 animate-fade-in-up min-w-0">
              <div className="flex items-center gap-3 text-gold">
                <Feather className="h-5 w-5" strokeWidth={1.5} />
                <span className="font-latin italic text-sm tracking-widest uppercase">
                  Sefer · ספר
                </span>
              </div>
              <h1 className="font-heading text-[2.25rem] sm:text-5xl md:text-5xl lg:text-6xl xl:text-7xl leading-[1.1] sm:leading-[1.05] text-foreground break-words">
                {t("landing.hero_line_1")}
                <br />
                <span className="text-gold">{t("landing.hero_line_2")}</span>
                <br />
                <span className="font-verse">{t("landing.hero_line_3")}</span>
              </h1>
              <p className="max-w-xl text-sm sm:text-base md:text-base lg:text-lg text-muted-foreground leading-relaxed break-words">
                {t("landing.hero_paragraph")}
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  data-testid="hero-start-reading"
                  onClick={openBook}
                  className="sacred-btn text-base px-6 py-6"
                >
                  <BookOpen className="h-4 w-4" strokeWidth={1.5} />
                  {t("landing.open_book_btn")}
                </Button>
                <Button
                  data-testid="hero-open-library"
                  onClick={() => navigate("/library")}
                  variant="ghost"
                  className="sacred-btn-ghost text-base px-6 py-6"
                >
                  {t("landing.open_library_btn")}
                  <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
                </Button>
              </div>

              {/* Progress pill */}
              {progress && (
                <Link
                  to={`/reader/${bookUrl(progress.book)}/${progress.chapter}?v=${progress.verse}`}
                  data-testid="progress-pill"
                  className="inline-flex items-center gap-3 rounded-sm border border-gold/30 bg-gold/5 px-4 py-2.5 hover:border-gold/60 transition-colors"
                >
                  <Scroll className="h-4 w-4 text-gold" strokeWidth={1.5} />
                  <span className="font-heading text-sm text-foreground">
                    {/* In EN mode use Sefaria English book name + Western
                        chapter/verse numerals so the progress pill reads
                        natively. Hebrew mode keeps the gematria-letter
                        formatting it has always shipped with. */}
                    {isEn
                      ? t("landing.continue_template", {
                          book: progress.book,
                          ch: progress.chapter,
                          v: progress.verse,
                        })
                      : t("landing.continue_template", {
                          book: bookHe(progress.book),
                          ch: numberToHebrew(progress.chapter),
                          v: numberToHebrew(progress.verse),
                        })}
                  </span>
                </Link>
              )}
            </div>

            {/* Closed 3D Book — the main entry point */}
            <div
              className="md:col-span-5 flex items-center justify-center animate-fade-in-up min-w-0"
              style={{ animationDelay: "160ms" }}
            >
              <div
                className="relative"
                data-testid="landing-book-cover"
                role="button"
                aria-label={t("landing.open_book_aria")}
              >
                <BookCover
                  size={bookSize}
                  title={t("landing.book_cover_title")}
                  subtitle={t("landing.book_cover_subtitle")}
                  onOpen={openBook}
                />
                <p className="mt-12 text-center font-latin italic text-xs tracking-[0.35em] uppercase text-gold/80">
                  {t("landing.tap_to_begin")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ORNAMENT */}
      <div className="mx-auto max-w-7xl px-6">
        <div className="ornamental-divider text-base">✦ ❖ ✦</div>
      </div>

      {/* Parasha + Daily Verse row */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 pt-16 grid md:grid-cols-2 gap-6">
        {/* This week's Parasha — with prev/next browsing (Feb 27, 2026).
            The original "currently displayed" parasha lives in `parasha`,
            which starts as this week's pick and can be navigated forward/
            backward through the 54-parasha cycle without leaving the page.
            `parashaOffset` tracks how far we've strayed from "this week"
            so the header label and the "back to current" pill know what
            to show. */}
        {parasha && (
          <Link
            to={`/reader/${bookUrl(parasha.book)}/${parasha.start[0]}`}
            data-testid="parasha-card"
            className={`relative rounded-sm border border-gold/30 bg-card shadow-book p-6 md:p-8 space-y-4 overflow-hidden block hover:border-gold/60 transition-all group ${parashaSwapping ? "opacity-60" : "opacity-100"}`}
            style={{ transition: "opacity 180ms ease-out, border-color 180ms ease-out" }}
          >
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-l from-transparent via-gold/60 to-transparent" />

            {/* Header row: prev arrow · label · next arrow.
                Arrows are nested inside the <Link> so we explicitly
                stopPropagation + preventDefault on click to avoid
                navigating to the reader. */}
            <div className="flex items-center justify-between gap-2">
              {/* "Previous parasha" — visually on the right in RTL */}
              <button
                type="button"
                data-testid="parasha-prev-btn"
                aria-label={t("landing.parasha_aria_prev")}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigateParasha(-1);
                }}
                disabled={parashaSwapping || !parashaList.length}
                className="parasha-nav-arrow inline-flex items-center justify-center h-7 w-7 rounded-sm text-gold/70 hover:text-gold hover:bg-gold/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
              </button>

              <div className="flex items-center gap-2 text-gold">
                <Feather className="h-4 w-4" strokeWidth={1.5} />
                <span
                  data-testid="parasha-card-subtitle"
                  className="font-heading text-sm tracking-widest uppercase"
                >
                  {parashaOffset === 0
                    ? t("landing.parasha_eyebrow_this_week")
                    : parashaOffset === -1
                    ? t("landing.parasha_eyebrow_prev")
                    : parashaOffset === 1
                    ? t("landing.parasha_eyebrow_next")
                    : parashaOffset < 0
                    // For multi-step drift the EN copy uses Arabic numerals
                    // ("3 Parashot back"); the HE copy keeps the gematria
                    // letters it has always used. Same i18n key, two
                    // locale-appropriate substitutions.
                    ? t("landing.parasha_eyebrow_n_back", {
                        n: isEn ? Math.abs(parashaOffset) : numberToHebrew(Math.abs(parashaOffset)),
                      })
                    : t("landing.parasha_eyebrow_n_forward", {
                        n: isEn ? parashaOffset : numberToHebrew(parashaOffset),
                      })}
                </span>
                {/* "Back to current" — only when we've drifted. Small,
                    same gold tone, never competes with main arrows. */}
                {parashaOffset !== 0 && (
                  <button
                    type="button"
                    data-testid="parasha-back-to-current"
                    aria-label={t("landing.parasha_back_to_current_aria")}
                    title={t("landing.parasha_back_to_current_aria")}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      resetToCurrentParasha();
                    }}
                    disabled={parashaSwapping}
                    className="inline-flex items-center justify-center h-5 w-5 rounded-sm text-gold/70 hover:text-gold hover:bg-gold/15 transition-colors"
                  >
                    <RotateCcw className="h-3 w-3" strokeWidth={1.75} />
                  </button>
                )}
              </div>

              {/* "Next parasha" — visually on the left in RTL */}
              <button
                type="button"
                data-testid="parasha-next-btn"
                aria-label={t("landing.parasha_aria_next")}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigateParasha(1);
                }}
                disabled={parashaSwapping || !parashaList.length}
                className="parasha-nav-arrow inline-flex items-center justify-center h-7 w-7 rounded-sm text-gold/70 hover:text-gold hover:bg-gold/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </div>

            <h3
              data-testid="parasha-name"
              className="font-heading text-3xl sm:text-4xl text-foreground break-words"
            >
              {/* EN: use Sefaria's English transliteration ("Korach") from
                  the parasha slug. HE: keep the Hebrew name byte-identical. */}
              {t("landing.parasha_name_template", { name: isEn ? parasha.slug : parasha.he })}
            </h3>
            <p className="font-latin italic text-xs sm:text-sm text-muted-foreground break-words">
              {/* Book + range. EN renders "Numbers · 16:1 – 18:32" with
                  arabic numerals; HE keeps the gematria letters used in
                  every printed luach. */}
              {isEn
                ? `${parasha.book} · ${parasha.start[0]}:${parasha.start[1]} – ${parasha.end[0]}:${parasha.end[1]}`
                : `${parasha.book_he} · ${numberToHebrew(parasha.start[0])}:${numberToHebrew(parasha.start[1])} – ${numberToHebrew(parasha.end[0])}:${numberToHebrew(parasha.end[1])}`}
            </p>

            {(isEn ? (parasha.opening_verse?.en || parasha.opening_verse?.he) : parasha.opening_verse?.he) && (
              <>
                <div className="ornamental-divider">✦</div>
                <p
                  dir={isEn ? "ltr" : "rtl"}
                  className={`torah-verse text-lg md:text-xl text-foreground leading-loose line-clamp-3 ${isEn ? "" : "hebrew-text"}`}
                >
                  {isEn ? (parasha.opening_verse?.en || parasha.opening_verse?.he) : parasha.opening_verse.he}
                </p>
              </>
            )}

            {/* Add the entire parasha to the lesson basket — one click. */}
            <div className="pt-2 space-y-2">
              <div onClick={(e) => e.preventDefault()} className="flex">
                <ParashaChiddushDialog parashaHe={parasha.he} parashaName={parasha.slug} />
              </div>

              {/* ── Aggadah / Story buttons (Feb 28, 2026) ──
                  Two-button row that replaces the old auto-expanded
                  AggadahCard. Each button pops a modal with the relevant
                  content. "סיפור" is admin-gated until the rabbi
                  approves the format — we hide the button entirely for
                  regular users so the experience stays clean and they
                  don't see locked / disabled UI. */}
              <div className="flex flex-col sm:flex-row gap-2"
                   onClick={(e) => e.preventDefault()}>
                <button
                  type="button"
                  data-testid="parasha-open-aggadah-btn"
                  onClick={(e) => {
                    e.preventDefault(); e.stopPropagation();
                    setParashaContentMode("aggadah");
                  }}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-sm border border-gold/40 hover:border-gold hover:bg-gold/10 text-foreground py-2.5 px-4 text-xs sm:text-sm font-heading transition-colors"
                >
                  <Scroll className="h-4 w-4 text-gold" strokeWidth={1.5} />
                  {t("landing.from_chazal")}
                </button>
                <button
                  type="button"
                  data-testid="parasha-open-story-btn"
                  onClick={(e) => {
                    e.preventDefault(); e.stopPropagation();
                    setParashaContentMode("story");
                  }}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-sm border border-gold/40 hover:border-gold hover:bg-gold/10 text-foreground py-2.5 px-4 text-xs sm:text-sm font-heading transition-colors"
                >
                  <Sparkles className="h-4 w-4 text-gold" strokeWidth={1.5} />
                  {t("landing.story_for_parasha")}
                </button>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <Button
                  onClick={handleAddParashaToBasket}
                  disabled={parashaAdding}
                  variant="outline"
                  size="sm"
                  className="flex-1 border-gold/40 hover:bg-gold/10 hover:border-gold text-foreground gap-2 whitespace-normal h-auto py-2.5 text-xs sm:text-sm leading-snug text-center"
                  data-testid="parasha-add-to-basket-btn"
                >
                  <ShoppingBasket className="h-4 w-4 shrink-0" strokeWidth={1.5} />
                  <span className="break-words">
                    {parashaAdding
                      ? t("landing.adding_to_basket")
                      : t("landing.add_full_parasha_to_basket", { name: isEn ? parasha.slug : parasha.he })}
                  </span>
                </Button>
                {/* Save the parasha's *opening* verse with parasha context.
                    Anchored to the first verse so it shows up under the
                    proper sefer in the notebook, with the parasha name
                    as the source_label. */}
                {parasha.opening_verse?.he && (
                  <SaveToNotebookButton
                    variant="chip"
                    testIdSuffix="parasha"
                    className="sm:w-auto justify-center"
                    payload={{
                      source_type: "verse",
                      source_book: parasha.book,
                      source_chapter: parasha.start[0],
                      source_verse: parasha.start[1],
                      source_ref_he: formatHebrewRef(
                        parasha.book_he || parasha.book,
                        parasha.start[0],
                        parasha.start[1]
                      ),
                      source_label: t("landing.parasha_notebook_label", { name: isEn ? parasha.slug : parasha.he }),
                      content_he: parasha.opening_verse.he,
                      content_text: parasha.opening_verse.en || "",
                    }}
                  />
                )}
              </div>
            </div>
          </Link>
        )}

        {/* Parasha content modal — opens for either "אגדה" or "סיפור".
            Mounted ONCE per page so prev/next nav on the parasha card
            just re-targets the same dialog. */}
        {parasha?.slug && (
          <ParashaContentDialog
            open={!!parashaContentMode}
            onClose={() => setParashaContentMode(null)}
            mode={parashaContentMode || "aggadah"}
            slug={parasha.slug}
            parashaHe={parasha.he}
            lang={isEn ? "en" : "he"}
          />
        )}

        {/* Daily verse */}
        <Link
          data-testid="daily-verse-card"
          to="/daily"
          className="relative rounded-sm border border-gold/30 bg-card shadow-book p-6 md:p-8 space-y-4 overflow-hidden block hover:border-gold/60 transition-all group"
        >
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-l from-transparent via-gold/60 to-transparent" />
          {/* Floating save-to-notebook — top-left so it doesn't fight the
              "click to open" affordance on the right. Clicks bubble are
              cancelled inside SaveToNotebookButton. */}
          {daily && (
            <div className="absolute top-3 left-3 z-10">
              <SaveToNotebookButton
                variant="icon"
                testIdSuffix="daily-card"
                payload={{
                  source_type: "daily",
                  source_book: daily.book,
                  source_chapter: daily.chapter,
                  source_verse: daily.verse,
                  source_ref_he:
                    daily.book_he && daily.chapter
                      ? formatHebrewRef(daily.book_he, daily.chapter, daily.verse)
                      : undefined,
                  source_label: t("landing.daily_verse_label"),
                  content_he: daily.he || "",
                  content_text: daily.en || "",
                }}
              />
            </div>
          )}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-gold">
              <Sparkles className="h-4 w-4" strokeWidth={1.5} />
              <span className="font-heading text-sm tracking-widest uppercase">
                {t("landing.daily_verse_eyebrow")}
              </span>
            </div>
            <ArrowLeft className="h-4 w-4 text-gold opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={1.5} />
          </div>

          {daily ? (
            <>
              <p
                dir={isEn ? "ltr" : "rtl"}
                className={`torah-verse text-xl md:text-2xl text-foreground leading-loose line-clamp-4 ${isEn ? "" : "hebrew-text"}`}
              >
                {isEn ? (daily.en || stripNikud(daily.he || "")) : (daily.he || stripNikud(daily.he || ""))}
              </p>
              <div className="ornamental-divider">✦</div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <span className="font-heading text-xs sm:text-sm text-gold break-words">
                  {isEn
                    ? `${daily.book} · Chapter ${daily.chapter} · Verse ${daily.verse}`
                    : `${daily.book_he} · פרק ${numberToHebrew(daily.chapter)} · פסוק ${numberToHebrew(daily.verse)}`}
                </span>
                <span
                  data-testid="daily-verse-open"
                  className="font-heading text-xs sm:text-sm text-foreground group-hover:text-gold transition-colors whitespace-nowrap"
                >
                  {t("landing.daily_open_link")}
                </span>
              </div>
            </>
          ) : (
            <div className="space-y-3">
              <div className="h-6 shimmer-bg rounded" />
              <div className="h-6 shimmer-bg rounded w-4/5" />
              <div className="h-4 shimmer-bg rounded w-2/5" />
            </div>
          )}
        </Link>
      </section>

      {/* SECTIONS */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-20">
        <div className="grid gap-6 md:grid-cols-3" data-testid="sections-grid">
          {[
            {
              key: "torah",
              // Primary headline (h3) — Hebrew letterform in HE mode,
              // English transliteration in EN mode. The secondary label
              // (in the eyebrow above) is the opposite locale, so each
              // mode shows BOTH names and the user always sees a Sefaria-
              // standard label they recognise.
              he: t("landing.section_torah_he"),
              en: t("landing.section_torah_en"),
              desc: t("landing.section_torah_desc"),
              verses: t("landing.section_torah_verses"),
            },
            {
              key: "neviim",
              he: t("landing.section_neviim_he"),
              en: t("landing.section_neviim_en"),
              desc: t("landing.section_neviim_desc"),
              verses: t("landing.section_neviim_verses"),
            },
            {
              key: "ketuvim",
              he: t("landing.section_ketuvim_he"),
              en: t("landing.section_ketuvim_en"),
              desc: t("landing.section_ketuvim_desc"),
              verses: t("landing.section_ketuvim_verses"),
            },
          ].map((s, idx) => (
            <Link
              key={s.key}
              to="/library"
              data-testid={`section-card-${s.key}`}
              className="spine-card rounded-sm p-8 block text-right animate-fade-in-up"
              style={{ animationDelay: `${idx * 100}ms` }}
            >
              <div className="flex items-center justify-between mb-4">
                <span className="font-latin italic text-xs tracking-widest uppercase text-gold">
                  {s.en}
                </span>
                <Scroll className="h-5 w-5 text-gold" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-3xl sm:text-4xl text-foreground mb-3 break-words">{s.he}</h3>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed mb-4 break-words">
                {s.desc}
              </p>
              <div className="gold-rule my-4" />
              <span className="font-heading text-xs text-gold">{s.verses}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURES — "Why Torahya?" */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 pb-24" data-testid="features-section">
        <div className="text-center space-y-3 mb-14">
          <h2 className="font-heading text-xl sm:text-2xl text-gold tracking-widest">
            {t("landing.why_torahya")}
          </h2>
          <h3 className="font-heading text-3xl md:text-4xl text-foreground">
            {t("landing.one_place_headline")}
          </h3>
          <div className="ornamental-divider mx-auto max-w-md">✦</div>
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {[
            {
              t: t("landing.feature_book_title"),
              d: t("landing.feature_book_desc"),
              i: BookOpen,
              to: "/library",
              testid: "feature-book",
            },
            {
              t: t("landing.feature_notebook_title"),
              d: t("landing.feature_notebook_desc"),
              i: NotebookPen,
              to: "/notebook",
              testid: "feature-notebook",
            },
            {
              t: t("landing.feature_ai_title"),
              d: t("landing.feature_ai_desc"),
              i: Sparkles,
              to: "/search",
              testid: "feature-ai",
            },
            {
              t: t("landing.feature_calendar_title"),
              d: t("landing.feature_calendar_desc"),
              i: CalendarIcon,
              to: "/calendar",
              testid: "feature-calendar",
            },
          ].map((f, idx) => {
            const Icon = f.i;
            return (
              <Link
                key={f.t}
                to={f.to}
                style={{ animationDelay: `${idx * 80}ms` }}
                data-testid={f.testid}
                className="spine-card rounded-sm p-6 animate-fade-in-up block transition-all duration-300 hover:border-gold/60 hover:-translate-y-1 hover:shadow-[0_12px_28px_-12px_rgba(184,137,59,0.45)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/60"
              >
                <Icon className="h-6 w-6 text-gold mb-4 transition-transform group-hover:scale-110" strokeWidth={1.5} />
                <h3 className="font-heading text-lg sm:text-xl text-foreground mb-2 break-words">{f.t}</h3>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed break-words">{f.d}</p>
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}
